// =====================================================
// The comparison summary: the page Fredline adds after the redline (.docx), and the first
// page of the PDF redlines and the review page's Summary tab. One model, two renderings
// (WordprocessingML and HTML), so every output says the same thing.
//
// Litera's summary report lists the two file names, the date, and the changes by type
// (each label in its redline style) with a total. This gives that, and also: the changes at
// a glance (total, words inserted and deleted, passages moved, share of the text changed);
// each version's details (last saved by, when, length); how the comparison was run; table
// rows, footnotes and endnotes, headers and footers counted apart; every changed number,
// amount, percentage and date, old against new, with where it is; where the changes are,
// section by section; and short notes on reading the redline.
//
// The body's changes are counted as the Word writer recorded them (core/change-record.ts), the
// record the change list is built from too, so the .docx summary, the PDF and the review page
// agree: one change is one run of consecutive inserted (or deleted, or moved) words, a replaced
// word is a deletion and an insertion, a footnote mark or picture inserted or deleted on its own is
// one, a whole inserted or deleted paragraph or table row is one change, a paragraph split or
// merge is one, and a paragraph whose formatting changed is one. When the caller has the change
// list itself (the PDF and review page), its changes are counted directly; the app, which has no
// Word file, has the record made without one (recordChanges).
// =====================================================

import type { DiffResult, DiffParagraph, DiffEntry, ParsedDocument, RichParagraph } from '../types';
import { changedFigures, type FigureRow } from './figures';
import { labelOf } from './doc-layout';
import { tokenize, normalizeQuotes } from './diff-engine-core';
import { textGap } from './pieces';
import { formatDiffers } from './docx-tracking';
import { cellWordDiff, rowCellsComparable } from './docx-tables';
import { countedChanges, spacingChanges, type RecordedChange } from './change-record';

type Kind = 'insert' | 'delete' | 'move-from' | 'move-to' | 'format';

/** The parts of a change-list entry the summary reads. */
export interface SummaryChange { type: Kind; part: string; block?: number; incoming?: boolean; spacing?: boolean }

/** Footnotes, endnotes, headers and footers whose text changed (diffAncillaryContent). */
export interface SummaryAncillary {
  notes: { kind: 'footnote' | 'endnote'; id: string; diff: DiffEntry[]; formats?: number }[];
  headers: { kind: string; type: string; diff: DiffEntry[] }[];
  headerWarnings: number;
  /** Text boxes whose text differs (not marked in the redline: differingTextBoxes). */
  textBoxWarnings?: number;
}

export interface SummaryInput {
  diff: DiffResult;
  orig: ParsedDocument;
  rev: ParsedDocument;
  origName: string;
  revName: string;
  generatedAt: Date;
  output: 'track' | 'combine' | 'markup';
  author?: string;
  /** Options the comparison ran with, in words ("Classic Word colours"). */
  options?: string[];
  ancillary?: SummaryAncillary;
  /** The change list's changes, when the caller has them: counted directly. */
  changes?: SummaryChange[];
  /** Otherwise the body's changes as the writer recorded them (the .docx's own record, or
   *  recordChanges made for the same output). Needed when `changes` is not given. */
  record?: RecordedChange[];
  /** Body blocks the writer recorded a formatting change on (track output). Without it,
   *  an unchanged paragraph whose formatting differs counts as one. */
  formatBlocks?: Set<number>;
  /** Changes in headers and footers as written (countRevisionGroups over their parts). */
  headerChanges?: number;
}

export interface SummaryDoc { role: 'Original' | 'Revised'; name: string; savedBy?: string; modified?: string; words: number }
export interface SummaryTypeRow { key: string; label: string; count: number; mark: 'ins' | 'del' | 'moveFrom' | 'moveTo' | 'plain' }
export interface SummarySection { heading: string; block: number; changes: number; inserted: number; deleted: number; moved: number; format: number }
export interface SummaryModel {
  title: string;
  docs: SummaryDoc[];
  generated: string;
  shownAs: string;
  options: string[];
  glance: { changes: number; inserted: number; deleted: number; moved: number; changedPct: string };
  types: SummaryTypeRow[];
  total: number;
  figures: FigureRow[];
  sections: SummarySection[];
  notes: string[];
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** "October 23, 2026, 2:32 PM", local time (no locale data involved, so every Node and browser agrees). */
export function formatWhen(d: Date): string {
  const hr = d.getHours(), m = d.getMinutes();
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${hr % 12 || 12}:${String(m).padStart(2, '0')} ${hr < 12 ? 'AM' : 'PM'}`;
}
function formatDay(iso: string | undefined): string | undefined {
  const m = iso && /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  // a placeholder some tools write for "no date" (1900-01-01, 1601-01-01, 0001-01-01) is no date
  if (!m || Number(m[1]) < 1980 || Number(m[2]) < 1 || Number(m[2]) > 12) return undefined;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}
const unxml = (s: string) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');
/** 1,234 (no locale data). */
const n = (v: number) => String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

async function docInfo(doc: ParsedDocument, role: 'Original' | 'Revised', name: string): Promise<SummaryDoc> {
  let words = 0;
  for (const p of doc.richParagraphs) words += tokenize(richText(p)).length;
  const out: SummaryDoc = { role, name, words };
  try {
    const core: string = (await doc.zip?.file('docProps/core.xml')?.async('string')) || '';
    // (any prefix, or none: some tools write the core-properties namespace as the default one,
    // <coreProperties xmlns="…"><lastModifiedBy>…)
    const tag = (t: string) => {
      const m = core.match(new RegExp(`<((?:[\\w.-]+:)?${t.split(':').pop()})\\b[^>]*>([^<]*)</\\1>`));
      return m ? unxml(m[2]).trim() : '';
    };
    out.savedBy = tag('cp:lastModifiedBy') || tag('dc:creator') || undefined;
    out.modified = formatDay(tag('dcterms:modified')) || formatDay(tag('dcterms:created'));
  } catch { /* no package properties (text or PDF input) */ }
  return out;
}

const isRow = (p: DiffParagraph) => !!(p.origRich?.isTableRow || p.revRich?.isTableRow);
const isPass = (p: DiffParagraph) => !!(p.origRich?.isPassthrough || p.revRich?.isPassthrough);
const richText = (r: RichParagraph | undefined) => !r ? '' : (r.cellData ? r.cellData.cells.map(c => c.text).join(' ') : (r.isTableRow ? r.text.replace(/ \| /g, ' ') : r.text));
/** Visible characters, as the change list compares them. */
const nwc = (s: string | null | undefined) => normalizeQuotes(s || '').replace(/[​-‏﻿⁠­͏؜]/g, '').replace(/\s+/g, '');

/** A note's or a header's word diff as changes: runs of consecutive same-type entries (as the
 *  writer records a paragraph's changes: diffRunsXml). */
function runsOf(entries: DiffEntry[], mergeMoveTo: boolean): Kind[] {
  type R = { type: Kind; chars: string; moveId?: number };
  const runs: R[] = [];
  let prev: R | null = null, moveTo: R | null = null;
  for (const e of entries) {
    if (e.type === 'equal') { prev = null; continue; }
    const type = e.type as Kind, moveId = (e as any).moveId;
    if (mergeMoveTo && type === 'move-to') {
      if (!moveTo) runs.push(moveTo = { type, chars: '' });
      moveTo.chars += nwc(e.value);
      prev = null;
      continue;
    }
    if (prev && prev.type === type && prev.moveId === moveId) { prev.chars += nwc(e.value); continue; }
    runs.push(prev = { type, chars: nwc(e.value), moveId });
  }
  return runs.filter(r => r.chars).map(r => r.type);
}

/** Changes in a written part (a header, a footer): per paragraph, a run of adjacent revisions
 *  of one kind is one change, and a paragraph-mark or table-row revision is one. `author`:
 *  count only that author's. */
export function countRevisionGroups(xml: string, author?: string): number {
  const mine = (attrs: string) => !author || attrs.includes(`w:author="${x(author)}"`);
  let count = 0;
  for (const p of xml.match(/<w:p\b[^>]*>[\s\S]*?<\/w:p>/g) || []) {
    let depth = 0, prev: string | null = null;
    for (const m of p.matchAll(/<(\/?)w:(ins|del|moveFrom|moveTo|r)\b([^>]*?)(\/?)>/g)) {
      const [, close, tag, attrs, self] = m;
      if (tag === 'r') { if (!close && !self && depth === 0) prev = null; continue; }
      if (self) { if (mine(attrs)) count++; continue; }   // a paragraph mark or row
      if (close) { depth = Math.max(0, depth - 1); continue; }
      if (depth++ === 0 && mine(attrs) && prev !== tag) { count++; prev = tag; }
    }
  }
  return count;
}

function snippet(text: string, words = 8): string {
  const w = text.replace(/\s+/g, ' ').trim().split(' ');
  return w.slice(0, words).join(' ') + (w.length > words ? '…' : '');
}
/** "2.1 Client shall pay…": the paragraph's number (if any) and its first words. */
function whereOf(p: DiffParagraph, block: number): string {
  const rich = p.revRich || p.origRich;
  if (!rich) return `Paragraph ${block + 1}`;
  const label = labelOf(rich)?.text;
  return `${label ? label + ' ' : ''}${snippet(rich.cellData ? rich.cellData.cells.map(c => c.text).join(' | ') : rich.text)}`;
}
/** A section start. Tier 1: a heading or title style, or an outline level. Tier 2, for documents
 *  without heading styles: a top-level numbered paragraph, or one that opens with a section
 *  number ("1.", "Section 4", "ARTICLE II"). */
function headingText(rich: RichParagraph | undefined, tier: 1 | 2): string | null {
  // (a line with no letters at all, "4. ________ ________" in a form, is a blank to fill in)
  if (!rich || rich.isTableRow || rich.isPassthrough || !(rich.text || '').trim() || !/\p{L}/u.test(rich.text)) return null;
  const ppr = rich.pPrXml || '';
  const styled = /<w:pStyle w:val="[^"]*(?:Heading|Title)[^"]*"/i.test(ppr) || /<w:outlineLvl w:val="[0-8]"/.test(ppr);
  const numbered = /<w:ilvl w:val="0"\/>/.test(ppr) && /<w:numId w:val="[1-9]/.test(ppr);
  const typed = /^\s*(?:(?:ARTICLE|Article|SECTION|Section)\s+[\dIVXLC]+\b|\d{1,2}\.\s+\S)/.test(rich.text);
  if (tier === 1 ? !styled : !(numbered || typed)) return null;
  const label = labelOf(rich)?.text;
  const t = `${label ? label + ' ' : ''}${tier === 1 ? rich.text.replace(/\s+/g, ' ').trim() : snippet(rich.text, 8)}`;
  // (cut by character, not UTF-16 unit: half an emoji is written to the .docx as U+FFFD)
  const chars = Array.from(t);
  return chars.length > 70 ? chars.slice(0, 69).join('').trimEnd() + '…' : t;
}

/** One side of a word diff as text, with the character ranges of its insertions or deletions. */
function sideText(diff: DiffEntry[], side: 'old' | 'new'): { text: string; ranges: [number, number][] } {
  let text = '', first = true;
  const ranges: [number, number][] = [];
  for (const d of diff) {
    const show = d.type === 'equal' || (side === 'old' ? (d.type === 'delete' || d.type === 'move-from') : (d.type === 'insert' || d.type === 'move-to'));
    if (!show || !d.value) continue;
    text += textGap(d, first);
    first = false;
    const s = text.length;
    text += d.value;
    if (d.type === 'delete' || d.type === 'insert') ranges.push([s, text.length]);
  }
  return { text, ranges };
}

interface Tally { insert: number; delete: number; 'move-from': number; 'move-to': number; format: number; rowsIns: number; rowsDel: number; notes: number; headers: number; incoming: number }

export async function buildSummaryModel(input: SummaryInput): Promise<SummaryModel> {
  const { diff, output } = input;
  const tracked = output !== 'markup';
  const anc: SummaryAncillary = input.ancillary || { notes: [], headers: [], headerWarnings: 0 };
  const t: Tally = { insert: 0, delete: 0, 'move-from': 0, 'move-to': 0, format: 0, rowsIns: 0, rowsDel: 0, notes: 0, headers: 0, incoming: 0 };
  const perBlock = new Map<number, { ins: number; del: number; move: number; format: number }>();
  const bump = (block: number, k: Kind) => {
    const b = perBlock.get(block) || { ins: 0, del: 0, move: 0, format: 0 };
    if (k === 'insert') b.ins++; else if (k === 'delete') b.del++; else if (k === 'format') b.format++; else b.move++;
    perBlock.set(block, b);
  };
  const wholeRow = (p: DiffParagraph | undefined) => !!p && isRow(p) && (p.type === 'para-insert' || p.type === 'para-delete');

  // tracked tabs and line breaks (not counted as changes; named in the notes)
  let spacing = 0;
  if (input.changes) {
    for (const c of input.changes) {
      if (c.spacing) { spacing++; continue; }
      if (c.incoming) t.incoming++;
      if (c.part === 'footnote' || c.part === 'endnote') { t.notes++; continue; }
      if (c.part === 'header' || c.part === 'footer') { t.headers++; continue; }
      const p = c.block != null ? diff.paragraphs[c.block] : undefined;
      if (wholeRow(p) && c.type === 'insert') t.rowsIns++;
      else if (wholeRow(p) && c.type === 'delete') t.rowsDel++;
      else t[c.type]++;
      if (c.block != null) bump(c.block, c.type);
    }
  } else {
    if (!input.record) throw new Error('the summary needs the change list or the change record (recordChanges)');
    spacing = spacingChanges(input.record);
    // the body, as the writer recorded it
    for (const c of countedChanges(input.record, tracked)) {
      const p = diff.paragraphs[c.block];
      if (c.scope === 'whole' && wholeRow(p) && c.type === 'insert') t.rowsIns++;
      else if (c.scope === 'whole' && wholeRow(p) && c.type === 'delete') t.rowsDel++;
      else t[c.type]++;
      bump(c.block, c.type);
    }
    // a paragraph whose formatting changed is one change (tracked output: a paragraph edited or
    // kept, not one inserted, deleted or replaced whole)
    if (tracked) diff.paragraphs.forEach((p, pi) => {
      if ((p as any)._freeTierDivider) return;
      const kept = p.type === 'equal' || (p.type === 'modify' && !isPass(p) && !(isRow(p) && !rowCellsComparable(p))) || (p.type === 'move-to' && !!p.diff && !isRow(p));
      if (kept && (input.formatBlocks ? input.formatBlocks.has(pi) : (p.type === 'equal' && formatDiffers(p.origRich, p.revRich)))) { t.format++; bump(pi, 'format'); }
    });
    for (const a of anc.notes) t.notes += runsOf(a.diff, false).length + (tracked ? a.formats || 0 : 0);
    if (input.headerChanges != null) t.headers = input.headerChanges;
    else for (const a of anc.headers) t.headers += runsOf(a.diff, false).length;
  }

  // changed figures, and where the changes are
  const figures: FigureRow[] = [];
  const sections: SummarySection[] = [];
  let current: SummarySection = { heading: 'Start of the document', block: 0, changes: 0, inserted: 0, deleted: 0, moved: 0, format: 0 };
  let headings = 0;
  const styledHeadings = diff.paragraphs.filter(p => headingText(p.revRich, 1) || headingText(p.origRich, 1)).length;
  const tier: 1 | 2 = styledHeadings >= 2 ? 1 : 2;
  diff.paragraphs.forEach((p, i) => {
    const hd = headingText(p.revRich, tier) || headingText(p.origRich, tier);
    if (hd) { headings++; current = { heading: hd, block: i, changes: 0, inserted: 0, deleted: 0, moved: 0, format: 0 }; }
    const b = perBlock.get(i);
    if (b) {
      if (!sections.includes(current)) sections.push(current);
      current.inserted += b.ins; current.deleted += b.del; current.moved += b.move; current.format += b.format;
      current.changes += b.ins + b.del + b.move + b.format;
    }
    if (p.type === 'equal' || (p as any)._freeTierDivider) return;
    const where = whereOf(p, i);
    if (p.type === 'para-insert' || p.type === 'para-delete') {
      // a whole new or deleted passage: its amounts and percentages (its dates and other
      // numbers are plain to see in the passage itself, and would bury the rest)
      const text = richText(p.type === 'para-insert' ? p.revRich : p.origRich);
      const rows = !text.trim() ? [] : p.type === 'para-insert' ? changedFigures('', [], text, [[0, text.length]], i, where) : changedFigures(text, [[0, text.length]], '', [], i, where);
      figures.push(...rows.filter(r => r.kind === 'Amount' || r.kind === 'Percentage'));
    } else if (p.type === 'modify' && isRow(p) && rowCellsComparable(p)) {
      const oc = p.origRich!.cellData!.cells, rc = p.revRich!.cellData!.cells;
      for (let k = 0; k < Math.max(oc.length, rc.length); k++) {
        const d = cellWordDiff(oc[k]?.text || '', rc[k]?.text || '');
        const o = sideText(d, 'old'), w = sideText(d, 'new');
        figures.push(...changedFigures(o.text, o.ranges, w.text, w.ranges, i, where));
      }
    } else if ((p.type === 'modify' || p.type === 'move-to') && p.diff) {
      const o = sideText(p.diff, 'old'), w = sideText(p.diff, 'new');
      figures.push(...changedFigures(o.text, o.ranges, w.text, w.ranges, i, where));
    } else if (p.type === 'modify') {
      const o = richText(p.origRich), w = richText(p.revRich);
      figures.push(...changedFigures(o, [[0, o.length]], w, [[0, w.length]], i, where));
    }
  });
  for (const a of anc.notes) {
    const o = sideText(a.diff, 'old'), w = sideText(a.diff, 'new');
    figures.push(...changedFigures(o.text, o.ranges, w.text, w.ranges, null, `${a.kind === 'footnote' ? 'Footnote' : 'Endnote'} ${a.id}`));
  }
  for (const a of anc.headers) {
    const o = sideText(a.diff, 'old'), w = sideText(a.diff, 'new');
    figures.push(...changedFigures(o.text, o.ranges, w.text, w.ranges, null, `${a.kind === 'header' ? 'Header' : 'Footer'}${a.type && a.type !== 'default' ? ` (${a.type} pages)` : ''}`));
  }

  const rows: SummaryTypeRow[] = [
    { key: 'insert', label: 'Inserted', count: t.insert, mark: 'ins' },
    { key: 'delete', label: 'Deleted', count: t.delete, mark: 'del' },
    { key: 'move-from', label: 'Moved from', count: t['move-from'], mark: 'moveFrom' },
    { key: 'move-to', label: 'Moved to', count: t['move-to'], mark: 'moveTo' },
    { key: 'rowsIns', label: 'Table rows inserted', count: t.rowsIns, mark: 'ins' },
    { key: 'rowsDel', label: 'Table rows deleted', count: t.rowsDel, mark: 'del' },
    { key: 'format', label: 'Formatting changed', count: t.format, mark: 'plain' },
    { key: 'notes', label: 'In footnotes and endnotes', count: t.notes, mark: 'plain' },
    { key: 'headers', label: 'In headers and footers', count: t.headers, mark: 'plain' },
  ];
  const types = rows.filter(r => r.count > 0 || ['insert', 'delete', 'move-from', 'move-to'].includes(r.key) || (r.key === 'format' && tracked));
  const total = types.reduce((a, r) => a + r.count, 0);

  const s = diff.stats;
  const all = (s.additions || 0) + (s.deletions || 0) + (s.unchanged || 0);
  const pct = all ? ((s.additions || 0) + (s.deletions || 0)) / all * 100 : 0;
  const docs = [await docInfo(input.orig, 'Original', input.origName), await docInfo(input.rev, 'Revised', input.revName)];
  const author = (input.author || '').trim() || 'Fredline';

  const notes: string[] = [];
  if (output === 'markup') notes.push('This is a coloured markup: deletions red and struck through, insertions blue and double-underlined, moves green. It has no tracked changes to accept or reject, and formatting changes are not shown; the Track Changes redline records them.');
  else notes.push('In Word, Accept All Changes gives the revised document and Reject All Changes gives the original.');
  if (output === 'combine') notes.push(t.incoming
    ? `${n(t.incoming)} of these changes were already tracked in the revised document and keep their own authors.`
    : 'Tracked changes already in the revised document keep their own authors.');
  notes.push('A replaced word or phrase counts as one deletion and one insertion, as in Word. A moved passage counts once where it was and once where it went.');
  if (tracked && spacing) notes.push(`Word also shows ${n(spacing)} change${spacing === 1 ? '' : 's'} to spacing (tabs, line breaks or spaces between words), which ${spacing === 1 ? 'is' : 'are'} not counted here.`);
  if (anc.textBoxWarnings) notes.push(`${anc.textBoxWarnings} text box${anc.textBoxWarnings === 1 ? '' : 'es'} differ${anc.textBoxWarnings === 1 ? 's' : ''} between the drafts, but changes inside text boxes are not marked: check ${anc.textBoxWarnings === 1 ? 'it' : 'them'} in both drafts.`);
  if (anc.headerWarnings) notes.push(`${anc.headerWarnings} header or footer part${anc.headerWarnings === 1 ? '' : 's'} differ${anc.headerWarnings === 1 ? 's' : ''} but could not be marked: check the headers and footers.`);

  return {
    title: input.revName.replace(/\.[a-z0-9]{2,5}$/i, ''),
    docs,
    generated: formatWhen(input.generatedAt),
    shownAs: output === 'markup' ? 'Coloured markup' : output === 'combine'
      ? `Tracked changes by ${author}, with the revised document's own tracked changes kept`
      : `Tracked changes by ${author}`,
    options: input.options || [],
    glance: { changes: total, inserted: s.additions || 0, deleted: s.deletions || 0, moved: t['move-to'], changedPct: pct >= 10 || pct === 0 ? `${Math.round(pct)}%` : pct < 0.1 ? '<0.1%' : `${pct.toFixed(1)}%` },
    types, total, figures,
    sections: headings >= 2 ? sections : [],
    notes,
  };
}

// ── WordprocessingML ─────────────────────────────────────────────────────────

const C = { ink: '1A1C2B', light: '4A4C5E', muted: '82849A', paper: 'F6F5F1', line: 'E3E1DA', red: 'C41E3A', blue: '1A4B8C', green: '2D6A4F' }; // redline ink, as in the Word markup
const FONT = 'Aptos';
const x = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

type Mark = SummaryTypeRow['mark'] | 'old' | 'new';
interface RunOpts { size?: number; bold?: boolean; color?: string; caps?: boolean; spacing?: number; mark?: Mark }
const markColor = (m: Mark | undefined) => m === 'ins' || m === 'new' ? C.blue : m === 'del' || m === 'old' ? C.red : m === 'moveFrom' || m === 'moveTo' ? C.green : undefined;
function run(text: string, o: RunOpts = {}): string {
  // every property set, so nothing comes from the document's own Normal style
  let rPr = `<w:rFonts w:ascii="${FONT}" w:hAnsi="${FONT}" w:eastAsia="${FONT}" w:cs="${FONT}"/>`;
  rPr += o.bold ? '<w:b/><w:bCs/>' : '<w:b w:val="0"/><w:bCs w:val="0"/>';
  rPr += '<w:i w:val="0"/><w:iCs w:val="0"/>';
  rPr += o.caps ? '<w:caps/>' : '<w:caps w:val="0"/><w:smallCaps w:val="0"/>';
  rPr += o.mark === 'del' || o.mark === 'old' ? '<w:strike/>' : '<w:strike w:val="0"/>';
  rPr += o.mark === 'moveFrom' ? '<w:dstrike/>' : '<w:dstrike w:val="0"/>';
  rPr += `<w:color w:val="${markColor(o.mark) || o.color || C.ink}"/>`;
  rPr += `<w:spacing w:val="${o.spacing || 0}"/>`;
  const sz = Math.round((o.size || 9.5) * 2);
  rPr += `<w:sz w:val="${sz}"/><w:szCs w:val="${sz}"/><w:highlight w:val="none"/>`;
  rPr += o.mark === 'ins' || o.mark === 'moveTo' ? '<w:u w:val="double"/>' : '<w:u w:val="none"/>';
  return `<w:r><w:rPr>${rPr}</w:rPr><w:t xml:space="preserve">${x(text)}</w:t></w:r>`;
}
interface ParaOpts { before?: number; after?: number; keepNext?: boolean; pageBreak?: boolean; jc?: 'left' | 'right'; rule?: string; pre?: string; post?: string }
function para(runs: string, o: ParaOpts = {}): string {
  let pPr = '';
  if (o.keepNext) pPr += '<w:keepNext/>';
  if (o.pageBreak) pPr += '<w:pageBreakBefore/>';
  pPr += '<w:widowControl/><w:numPr><w:ilvl w:val="0"/><w:numId w:val="0"/></w:numPr><w:suppressLineNumbers/>';
  if (o.rule) pPr += `<w:pBdr><w:bottom w:val="single" w:sz="6" w:space="3" w:color="${o.rule}"/></w:pBdr>`;
  pPr += `<w:spacing w:before="${o.before ?? 0}" w:after="${o.after ?? 0}" w:line="252" w:lineRule="auto"/>`;
  pPr += '<w:ind w:left="0" w:right="0" w:firstLine="0"/>';
  pPr += `<w:jc w:val="${o.jc || 'left'}"/>`;
  return `<w:p><w:pPr>${pPr}</w:pPr>${o.pre || ''}${runs}${o.post || ''}</w:p>`;
}
interface Cell { content: string; shade?: string }
interface Row { cells: Cell[]; header?: boolean; topRule?: boolean; noRule?: boolean }
function table(widths: number[], rows: Row[], o: { tiles?: boolean } = {}): string {
  const total = widths.reduce((a, b) => a + b, 0);
  const gap = o.tiles ? 50 : 0;
  const pad = o.tiles ? [110, 140] : [45, 60];
  let t = `<w:tbl><w:tblPr><w:tblW w:w="${total}" w:type="dxa"/>` +
    (gap ? `<w:tblCellSpacing w:w="${gap}" w:type="dxa"/><w:tblInd w:w="${-gap}" w:type="dxa"/>` : '<w:tblInd w:w="0" w:type="dxa"/>') +
    '<w:tblBorders><w:top w:val="nil"/><w:left w:val="nil"/><w:bottom w:val="nil"/><w:right w:val="nil"/><w:insideH w:val="nil"/><w:insideV w:val="nil"/></w:tblBorders>' +
    `<w:tblLayout w:type="fixed"/><w:tblCellMar><w:top w:w="${pad[0]}" w:type="dxa"/><w:left w:w="${pad[1]}" w:type="dxa"/>` +
    `<w:bottom w:w="${pad[0]}" w:type="dxa"/><w:right w:w="${pad[1]}" w:type="dxa"/></w:tblCellMar>` +
    '<w:tblLook w:val="0000" w:firstRow="0" w:lastRow="0" w:firstColumn="0" w:lastColumn="0" w:noHBand="1" w:noVBand="1"/></w:tblPr>' +
    `<w:tblGrid>${widths.map(w => `<w:gridCol w:w="${w}"/>`).join('')}</w:tblGrid>`;
  for (const r of rows) {
    t += `<w:tr><w:trPr><w:cantSplit/>${r.header ? '<w:tblHeader/>' : ''}${gap ? `<w:tblCellSpacing w:w="${gap}" w:type="dxa"/>` : ''}</w:trPr>`;
    r.cells.forEach((cell, k) => {
      const top = r.topRule ? `<w:top w:val="single" w:sz="8" w:space="0" w:color="${C.ink}"/>` : '<w:top w:val="nil"/>';
      const bottom = r.noRule || o.tiles ? '<w:bottom w:val="nil"/>' : `<w:bottom w:val="single" w:sz="${r.header ? 8 : 4}" w:space="0" w:color="${r.header ? C.ink : C.line}"/>`;
      t += `<w:tc><w:tcPr><w:tcW w:w="${widths[k]}" w:type="dxa"/><w:tcBorders>${top}<w:left w:val="nil"/>${bottom}<w:right w:val="nil"/></w:tcBorders>` +
        (cell.shade ? `<w:shd w:val="clear" w:color="auto" w:fill="${cell.shade}"/>` : '') +
        `<w:vAlign w:val="${o.tiles ? 'center' : 'top'}"/></w:tcPr>${cell.content}</w:tc>`;
    });
    t += '</w:tr>';
  }
  return t + '</w:tbl>';
}
const cellText = (runs: string, right = false) => para(runs, { jc: right ? 'right' : 'left' });
const heading = (text: string) => para(run(text, { size: 8, bold: true, caps: true, color: C.light, spacing: 24 }), { before: 320, after: 90, keepNext: true, rule: C.line });
const colHead = (text: string, right = false) => cellText(run(text, { size: 7, bold: true, caps: true, color: C.muted, spacing: 4 }), right);
const dash = () => run('—', { color: C.muted });
/** The Fredline wordmark ("Fred" in ink, "line" in insertion blue, double-underlined; as on
 *  fredline.app and in core/wordmark.ts), and a text with each "Fredline" in it shown that way. */
const wm = (size: number) => run('Fred', { size, bold: true, color: C.ink }) + run('line', { size, bold: true, mark: 'ins' });
const marked = (text: string, o: RunOpts = {}) => text.split(/\b(Fredline)\b/).map((p, i) => i % 2 ? wm(o.size || 9.5) : p ? run(p, o) : '').join('');
/** Column widths from fractions of the text width; the last column takes what is left. */
const cols = (w: number, fr: number[]) => { const a = fr.map(f => Math.round(w * f)); a.push(w - a.reduce((s, v) => s + v, 0)); return a; };

/** Bookmarks that mark the summary for tools (the audit title, tests). */
export const SUMMARY_BOOKMARK = '_FredlineSummary';
export const SUMMARY_TITLE_BOOKMARK = '_FredlineSummaryTitle';
export const AUDIT_RESULT_BOOKMARK = '_FredlineAuditResult';

/** The audit's result line, for under the summary's title (titleAuditRedline). */
export function auditLineDocx(line: string, id = 2147480002): string {
  return para(run(line, { size: 10.5, bold: true, color: C.red }), { after: 60,
    pre: `<w:bookmarkStart w:id="${id}" w:name="${AUDIT_RESULT_BOOKMARK}"/>`, post: `<w:bookmarkEnd w:id="${id}"/>` });
}
export const SUMMARY_FIGURE_LIMIT = 200;
export const SUMMARY_SECTION_LIMIT = 80;

/** The summary as WordprocessingML: paragraphs and tables, to follow a section break.
 *  `widthTw`: the page's text width. `newPage`: start with a page break (when the section
 *  break before it is a continuous one). Bookmark ids `idBase` and `idBase + 1` must be free. */
export function summaryDocx(m: SummaryModel, widthTw: number, o: { newPage?: boolean; idBase?: number } = {}): string {
  const w = Math.max(6000, Math.min(widthTw || 9360, 15840));
  const id = o.idBase ?? 2147480000;
  const out: string[] = [];
  out.push(para(wm(10) + run('  ·  Comparison summary', { size: 8, bold: true, caps: true, color: C.red, spacing: 30 }),
    { after: 50, pageBreak: o.newPage, pre: `<w:bookmarkStart w:id="${id}" w:name="${SUMMARY_BOOKMARK}"/>` }));
  out.push(para(run(m.title, { size: 20, bold: true }), { after: 30, pre: `<w:bookmarkStart w:id="${id + 1}" w:name="${SUMMARY_TITLE_BOOKMARK}"/>`, post: `<w:bookmarkEnd w:id="${id + 1}"/>` }));
  out.push(para(run(m.docs[0].name, { color: C.light }) + run('   →   ', { color: C.muted }) + run(m.docs[1].name, { color: C.light }) +
    run(`   ·   ${m.generated}`, { color: C.muted }), { after: 200 }));

  // at a glance
  const tiles: [string, string, Mark | undefined][] = [
    [n(m.glance.changes), m.glance.changes === 1 ? 'change' : 'changes', undefined],
    [n(m.glance.inserted), m.glance.inserted === 1 ? 'word inserted' : 'words inserted', 'ins'],
    [n(m.glance.deleted), m.glance.deleted === 1 ? 'word deleted' : 'words deleted', 'del'],
    [n(m.glance.moved), m.glance.moved === 1 ? 'passage moved' : 'passages moved', 'moveTo'],
    [m.glance.changedPct, 'of the text changed', undefined],
  ];
  out.push(table(cols(w, [0.2, 0.2, 0.2, 0.2]), [{ cells: tiles.map(([v, label, mark]) => ({
    shade: C.paper,
    content: para(run(v, { size: 17, bold: true, color: markColor(mark) || C.ink })) + para(run(label, { size: 7, caps: true, color: C.muted, spacing: 6 })),
  })) }], { tiles: true }));

  // the documents
  out.push(heading('Documents'));
  const dw = cols(w, [0.12, 0.34, 0.2, 0.2]);
  out.push(table(dw, [
    { header: true, cells: [colHead(''), colHead('File'), colHead('Last saved by'), colHead('Last modified'), colHead('Words', true)].map(content => ({ content })) },
    ...m.docs.map(d => ({ cells: [
      cellText(run(d.role, { bold: true })), cellText(run(d.name)),
      cellText(d.savedBy ? run(d.savedBy) : dash()), cellText(d.modified ? run(d.modified) : dash()), cellText(run(n(d.words)), true),
    ].map(content => ({ content })) })),
  ]));
  const kv: [string, string][] = [['Compared', m.generated], ['Shown as', m.shownAs]];
  if (m.options.length) kv.push(['Options', m.options.join('; ')]);
  const kw = cols(w, [0.12]);
  out.push(table(kw, kv.map(([k, v]) => ({ noRule: true, cells: [{ content: cellText(run(k, { size: 8.5, color: C.muted })) }, { content: cellText(marked(v, { size: 8.5 })) }] }))));

  // changes by type
  out.push(heading('Changes by type'));
  const tw = cols(w, [0.8]);
  out.push(table(tw, [
    ...m.types.map(r => ({ cells: [{ content: cellText(run(r.label, { mark: r.mark })) }, { content: cellText(run(n(r.count), { color: r.count ? C.ink : C.muted }), true) }] })),
    { topRule: true, noRule: true, cells: [{ content: cellText(run('Total', { bold: true })) }, { content: cellText(run(n(m.total), { bold: true }), true) }] },
  ]));

  // changed figures
  out.push(heading('Changed numbers, amounts, percentages and dates'));
  if (!m.figures.length) out.push(para(run('None. No number, amount, percentage or date changed.', { color: C.muted }), { after: 40 }));
  else {
    const fw = cols(w, [0.42, 0.13, 0.225]);
    const shown = m.figures.slice(0, SUMMARY_FIGURE_LIMIT);
    out.push(table(fw, [
      { header: true, cells: [colHead('Where'), colHead('Kind'), colHead('Original'), colHead('Revised')].map(content => ({ content })) },
      ...shown.map(f => ({ cells: [
        cellText(run(f.where, { size: 8.5, color: C.light })), cellText(run(f.kind, { size: 8.5, color: C.muted })),
        cellText(f.old ? run(f.old, { mark: 'old' }) : dash()), cellText(f.new ? run(f.new, { mark: 'new' }) : dash()),
      ].map(content => ({ content })) })),
    ]));
    if (m.figures.length > shown.length) out.push(para(run(`And ${n(m.figures.length - shown.length)} more.`, { size: 8.5, color: C.muted }), { before: 60 }));
  }

  // where the changes are
  if (m.sections.length) {
    out.push(heading('Where the changes are'));
    const sw = cols(w, [0.46, 0.11, 0.11, 0.11, 0.11]);
    const shown = m.sections.slice(0, SUMMARY_SECTION_LIMIT);
    const num = (v: number, color: string) => cellText(v ? run(n(v), { size: 9, color }) : run('·', { size: 9, color: C.muted }), true);
    out.push(table(sw, [
      { header: true, cells: [colHead('Section'), colHead('Changes', true), colHead('Inserted', true), colHead('Deleted', true), colHead('Moved', true), colHead('Format', true)].map(content => ({ content })) },
      ...shown.map(sc => ({ cells: [
        cellText(run(sc.heading, { size: 9 })),
        cellText(run(n(sc.changes), { size: 9, bold: true }), true),
        num(sc.inserted, C.blue), num(sc.deleted, C.red), num(sc.moved, C.green), num(sc.format, C.ink),
      ].map(content => ({ content })) })),
    ]));
    if (m.sections.length > shown.length) out.push(para(run(`And ${n(m.sections.length - shown.length)} more sections.`, { size: 8.5, color: C.muted }), { before: 60 }));
  }

  // notes
  out.push(heading('Reading this redline'));
  for (const t of m.notes) out.push(para(marked(t, { size: 8.5, color: C.light }), { after: 50 }));
  out.push(para(run('Compared with ', { size: 7.5, color: C.muted }) + wm(7.5), { before: 240, post: `<w:bookmarkEnd w:id="${id}"/>` }));
  return out.join('');
}

// ── HTML (the PDF redlines, the review page) ─────────────────────────────────

const h = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** The Fredline wordmark (styled by SUMMARY_CSS), and a text with each "Fredline" in it shown that way. */
const WM = '<span class="fs-wm">Fred<span>line</span></span>';
const hm = (s: string) => h(s).replace(/\bFredline\b/g, WM);

/** Styles for summaryHtml. Every class is prefixed "fs-" (and scoped to .fsum), so the page
 *  it sits in (the app, the review page) can't restyle it through a class of its own. */
export const SUMMARY_CSS = `
  .fsum { font-family: 'Instrument Sans', -apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1a1c2b; font-size: 9pt; line-height: 1.35; text-align: left; }
  .fsum p { margin: 0; }
  .fsum .fs-over { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; color: #c41e3a; margin: 0 0 4pt; }
  .fsum h1 { font-size: 19pt; line-height: 1.15; margin: 0 0 3pt; font-weight: 700; overflow-wrap: anywhere; color: #1a1c2b; }
  .fsum .fs-vs { color: #4a4c5e; margin: 0 0 12pt; overflow-wrap: anywhere; }
  .fsum .fs-sep { color: #82849a; padding: 0 5pt; }
  .fsum .fs-tiles { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 5pt; margin: 0; }
  .fsum .fs-tile { background: #f6f5f1; border-radius: 5pt; padding: 7pt 9pt; break-inside: avoid; }
  .fsum .fs-tile b { display: block; font-size: 16pt; line-height: 1.1; font-variant-numeric: tabular-nums; }
  .fsum .fs-tile span { display: block; font-size: 6.5pt; text-transform: uppercase; letter-spacing: 0.03em; color: #82849a; margin-top: 2pt; }
  .fsum .fs-t-ins b { color: #1a4b8c; } .fsum .fs-t-del b { color: #c41e3a; } .fsum .fs-t-move b { color: #2d6a4f; }
  .fsum h2 { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #4a4c5e; border-bottom: 0.75pt solid #e3e1da; padding-bottom: 3pt; margin: 15pt 0 4pt; break-after: avoid; }
  .fsum table { width: 100%; border-collapse: collapse; }
  .fsum th { text-align: left; font-size: 6.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #82849a; border-bottom: 1pt solid #1a1c2b; padding: 3pt 5pt; }
  .fsum td { border-bottom: 0.5pt solid #e3e1da; padding: 2.5pt 5pt; vertical-align: top; }
  .fsum td.fs-file, .fsum td.fs-where { overflow-wrap: anywhere; }
  .fsum table.fs-docs td:nth-child(3), .fsum table.fs-docs td:nth-child(4) { white-space: nowrap; }
  .fsum td.fs-kind { white-space: nowrap; width: 1%; }
  .fsum .fs-fig { white-space: nowrap; }
  .fsum tr { break-inside: avoid; }
  .fsum .fs-n { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; width: 1%; }
  .fsum table.fs-kv td { border: 0; padding: 1pt 5pt; } .fsum table.fs-kv td:first-child { color: #82849a; width: 13%; }
  .fsum tr.fs-total td { border-top: 1pt solid #1a1c2b; border-bottom: 0; font-weight: 700; }
  .fsum .fs-m-ins { color: #1a4b8c; text-decoration: underline double; } .fsum .fs-m-del { color: #c41e3a; text-decoration: line-through; }
  .fsum .fs-m-moveFrom { color: #2d6a4f; text-decoration: line-through double; } .fsum .fs-m-moveTo { color: #2d6a4f; text-decoration: underline double; }
  .fsum .fs-old { color: #c41e3a; text-decoration: line-through; } .fsum .fs-new { color: #1a4b8c; }
  .fsum [class*="fs-m-"], .fsum .fs-old { text-decoration-skip-ink: none; }
  .fsum .fs-c-ins { color: #1a4b8c; } .fsum .fs-c-del { color: #c41e3a; } .fsum .fs-c-move { color: #2d6a4f; }
  .fsum .fs-where { color: #4a4c5e; } .fsum .fs-dim { color: #82849a; } .fsum .fs-none { color: #82849a; margin: 3pt 0; }
  .fsum p.fs-note { margin: 0 0 3pt; color: #4a4c5e; font-size: 8.5pt; }
  .fsum .fs-foot { margin: 12pt 0 0; font-size: 7pt; color: #82849a; }
  .fsum a { color: inherit; text-decoration: none; }
  .fsum a:hover { text-decoration: underline; }
  .fsum .fs-wm { font-family: 'Schibsted Grotesk', 'Instrument Sans', -apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif; font-weight: 800; color: #1a1c2b; letter-spacing: normal; text-transform: none; white-space: nowrap; }
  .fsum .fs-wm span { color: #2a5bd7; text-decoration: underline double; text-decoration-color: #2a5bd7; text-decoration-thickness: 0.07em; text-underline-offset: 0.16em; text-decoration-skip-ink: none; }
  .fsum .fs-over .fs-wm { font-size: 1.35em; margin-right: 1pt; }
  @media (max-width: 600px) {
    .fsum .fs-tiles { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .fsum table.fs-docs :is(th, td):nth-child(3), .fsum table.fs-docs :is(th, td):nth-child(4),
    .fsum table.fs-sections :is(th, td):nth-child(n+3) { display: none; }
    .fsum table.fs-figures tr:first-child { display: none; }
    .fsum table.fs-figures tr { display: grid; grid-template-columns: auto minmax(0, 1fr) minmax(0, 1fr); border-bottom: 0.5pt solid #e3e1da; padding: 3pt 0; }
    .fsum table.fs-figures td { border: 0; padding: 1pt 4pt; width: auto; }
    .fsum table.fs-figures td.fs-where { grid-column: 1 / -1; }
    .fsum .fs-fig { white-space: normal; }
  }
`;

/** The summary as HTML (style it with SUMMARY_CSS). `link`: rows link to their paragraph
 *  (#p-<block>), as on the PDF, the review page and the app's full redline. */
export function summaryHtml(m: SummaryModel, link = false): string {
  const a = (block: number | null, text: string) => link && block != null ? `<a href="#p-${block}">${h(text)}</a>` : h(text);
  const plural = (v: number, one: string, many: string) => v === 1 ? one : many;
  const cls = (...c: (string | false)[]) => c.filter(Boolean).map(x => 'fs-' + x).join(' ');
  const tile = (kind: string, v: string, label: string) => `<div class="${cls('tile', kind && 't-' + kind)}"><b>${v}</b><span>${label}</span></div>`;
  let out = `<div class="fsum"><p class="fs-over">${WM} · Comparison summary</p><h1>${h(m.title)}</h1>` +
    `<p class="fs-vs">${h(m.docs[0].name)}<span class="fs-sep">→</span>${h(m.docs[1].name)}<span class="fs-sep">·</span>${h(m.generated)}</p>` +
    '<div class="fs-tiles">' +
    tile('', n(m.glance.changes), plural(m.glance.changes, 'change', 'changes')) +
    tile('ins', n(m.glance.inserted), plural(m.glance.inserted, 'word inserted', 'words inserted')) +
    tile('del', n(m.glance.deleted), plural(m.glance.deleted, 'word deleted', 'words deleted')) +
    tile('move', n(m.glance.moved), plural(m.glance.moved, 'passage moved', 'passages moved')) +
    tile('', h(m.glance.changedPct), 'of the text changed') + '</div>';
  const dimOr = (v: string | undefined) => v ? `<td>${h(v)}</td>` : '<td class="fs-dim">—</td>';
  out += '<h2>Documents</h2><table class="fs-docs"><tr><th></th><th>File</th><th>Last saved by</th><th>Last modified</th><th class="fs-n">Words</th></tr>' +
    m.docs.map(d => `<tr><td><b>${d.role}</b></td><td class="fs-file">${h(d.name)}</td>${dimOr(d.savedBy)}${dimOr(d.modified)}<td class="fs-n">${n(d.words)}</td></tr>`).join('') + '</table>' +
    `<table class="fs-kv"><tr><td>Compared</td><td>${h(m.generated)}</td></tr><tr><td>Shown as</td><td>${hm(m.shownAs)}</td></tr>` +
    (m.options.length ? `<tr><td>Options</td><td>${hm(m.options.join('; '))}</td></tr>` : '') + '</table>';
  out += '<h2>Changes by type</h2><table class="fs-types">' +
    m.types.map(t => `<tr><td><span class="${cls('m-' + t.mark)}">${h(t.label)}</span></td><td class="${cls('n', !t.count && 'dim')}">${n(t.count)}</td></tr>`).join('') +
    `<tr class="fs-total"><td>Total</td><td class="fs-n">${n(m.total)}</td></tr></table>`;
  const fig = (v: string, side: 'old' | 'new') => v ? `<span class="${cls(side, v.length <= 28 && 'fig')}">${h(v)}</span>` : '<span class="fs-dim">—</span>';
  out += '<h2>Changed numbers, amounts, percentages and dates</h2>' + (m.figures.length
    ? '<table class="fs-figures"><tr><th>Where</th><th>Kind</th><th>Original</th><th>Revised</th></tr>' + m.figures.map(f =>
        `<tr><td class="fs-where">${a(f.block, f.where)}</td><td class="fs-kind fs-dim">${f.kind}</td><td>${fig(f.old, 'old')}</td><td>${fig(f.new, 'new')}</td></tr>`).join('') + '</table>'
    : '<p class="fs-none">None. No number, amount, percentage or date changed.</p>');
  if (m.sections.length) {
    const num = (v: number, color: string) => `<td class="${cls('n', v ? color : 'dim')}">${v ? n(v) : '·'}</td>`;
    out += '<h2>Where the changes are</h2><table class="fs-sections"><tr><th>Section</th><th class="fs-n">Changes</th><th class="fs-n">Inserted</th><th class="fs-n">Deleted</th><th class="fs-n">Moved</th><th class="fs-n">Format</th></tr>' +
      m.sections.map(sc => `<tr><td>${a(sc.block, sc.heading)}</td><td class="fs-n"><b>${n(sc.changes)}</b></td>${num(sc.inserted, 'c-ins')}${num(sc.deleted, 'c-del')}${num(sc.moved, 'c-move')}${num(sc.format, '')}</tr>`).join('') + '</table>';
  }
  out += '<h2>Reading this redline</h2>' + m.notes.map(t => `<p class="fs-note">${hm(t)}</p>`).join('') + `<p class="fs-foot">Compared with ${WM}</p></div>`;
  return out;
}
